import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Eager: shell + landing + หน้าแรกหลัง login — ที่เหลือ lazy ทั้งหมด
// เพื่อแตก main chunk ตาม route (โหลดเฉพาะหน้าที่เข้าจริง)
import App from './App';
import StudentLookup from 'containers/registrations/StudentLookup';
import Profile from 'containers/profile/Profile';
import Dashboard from 'containers/profile/Dashboard';

// Registrations
const RegistrationForm = lazy(() => import('containers/registrations/RegistrationForm'));
const TcasForgotPassword = lazy(() => import('containers/registrations/TcasForgotPassword'));
const BlacklistForm = lazy(() => import('containers/registrations/BlacklistForm'));
const RedirectLoggedIn = lazy(() => import('containers/registrations/RedirectLoggedIn'));
const RedirectOldStudent = lazy(() => import('containers/registrations/RedirectOldStudent'));

// Profile
const ApplicantInfo = lazy(() => import('containers/profile/ApplicantInfo'));
const OfferAccept = lazy(() => import('containers/profile/OfferAccept'));
const OfferReject = lazy(() => import('containers/profile/OfferReject'));
const OfferCancel = lazy(() => import('containers/profile/OfferCancel'));
const ApplyRound3 = lazy(() => import('containers/profile/ApplyRound3'));
const SetZero = lazy(() => import('containers/profile/SetZero'));
const ReprocessRound3 = lazy(() => import('containers/profile/ReprocessRound3'));
const RedeemRound3 = lazy(() => import('containers/profile/RedeemRound3'));

// Folio
const FolioBuilder = lazy(() => import('./folio-builder/FolioBuilder'));
const FolioPdf = lazy(() => import('./containers/folio/FolioPdf'));

const RankCheckRound3 = lazy(() => import('containers/profile/RankCheckRound3'));
const RankingCheckContent = lazy(() =>
  import('containers/profile/RankCheckRound3').then((m) => ({ default: m.RankingCheckContent }))
);

// Exam
const TGATTPATSummary = lazy(() => import('containers/profile/apply-exams/TGATTPATSummary'));
const TGATTPATConfirmation = lazy(() => import('containers/profile/apply-exams/TGATTPATConfirmation'));
const TGATTPATDetail = lazy(() => import('containers/profile/apply-exams/TGATTPATDetail'));
const TGATTPATUpdateSites = lazy(() => import('containers/profile/apply-exams/TGATTPATUpdateSites'));
const TGATTPATPostpone = lazy(() => import('containers/profile/apply-exams/TGATTPATPostpone'));

// const TGATTPATCBTDetail = lazy(() => import('containers/profile/apply-exams/TGATTPATCBTDetail')); // TCAS70 ไม่มี CBT (G27-4)

const ALevelSummary = lazy(() => import('containers/profile/apply-exams/ALevelSummary'));
const ALevelConfirmation = lazy(() => import('containers/profile/apply-exams/ALevelConfirmation'));
const ALevelDetail = lazy(() => import('containers/profile/apply-exams/ALevelDetail'));
const ALevelUpdateSites = lazy(() => import('containers/profile/apply-exams/ALevelUpdateSites'));

// const ALevelSimDetail = lazy(() => import('containers/profile/apply-exams/ALevelSimDetail')); // SIM ซ่อนจนกว่าจะประกาศวัน TCAS70

const Exam4YVeto = lazy(() => import('containers/profile/apply-exams/Exam4YVeto'));

const ExamApplicationInfo = lazy(() => import('containers/profile/ExamApplicationInfo'));
const ExamScores = lazy(() => import('containers/profile/ExamScores'));
const ExamRecheck = lazy(() => import('containers/profile/ExamRecheck'));
const ExamRecheckConsent = lazy(() => import('containers/profile/ExamRecheckConsent'));
const ExamAnswers = lazy(() => import('containers/profile/ExamAnswers'));
const ExamAnswerDetail = lazy(() => import('containers/profile/ExamAnswerDetail'));

const ExamDispute = lazy(() => import('containers/profile/ExamDispute'));
const ExamDisputeConsent = lazy(() => import('containers/profile/ExamDisputeConsent'));

// QRCode Generator
const ExamResult = lazy(() => import('containers/ExamResult'));
const AdmissionPrepare = lazy(() => import('components/profile/enrollments/round3/AdmissionPrepare'));

const R3RedemptionReceipt = lazy(() => import('containers/R3RedemptionReceipt'));
const TGATRedemptionReceipt = lazy(() => import('containers/TGATRedemptionReceipt'));

export const renderStudentRoutes = () => {
  return (
    <Router>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<App />}>
          <Route index element={<StudentLookup />} />
          <Route path="/form" element={<RegistrationForm />} />
          <Route path="/veto/:token" element={<BlacklistForm />} />
          <Route path="/redirect-l/:token" element={<RedirectLoggedIn />} />
          <Route path="/redirect-o/:token" element={<RedirectOldStudent />} />
          {/* deep link เก่า /:cid/not-found — ฟอร์มเปิดโล่งไม่ผ่าน lookup ทำให้เกิด ticket
              create_found_student มั่ว (เด็กมีบัญชีแล้วก็ยื่นได้) → พากลับหน้า lookup ให้เดินตาม flow ปกติ */}
          <Route path="/:citizenId/not-found" element={<Navigate to="/" replace />} />
          <Route path="/:studentType/forgot-password" element={<TcasForgotPassword />} />
          <Route path="/_html/ranking-check" element={<RankingCheckContent />} />
        </Route>
        {/* ลิงก์แชร์แฟ้ม: อยู่นอก shell ของ App (ไม่มีแถบเมนู/โลโก้/footer) — PDF เต็มจอ ตัดสิน 2026-09-09 */}
        <Route path="/view-folio/:key" element={<FolioPdf />} />
        <Route path="/profile" element={<Profile />}>
          <Route path="/profile/applicant-info" element={<ApplicantInfo />} />
          <Route index element={<Dashboard />} />

          <Route path="/profile/tgattpat-postpone/confirm" element={<TGATTPATPostpone action="confirm" />} />
          <Route path="/profile/tgattpat-postpone/cancel" element={<TGATTPATPostpone action="cancel" />} />

          <Route path="/profile/apply-exams/tgattpat" element={<TGATTPATSummary />} />
          <Route path="/profile/apply-exams/tgattpat/confirmation" element={<TGATTPATConfirmation />} />
          <Route path="/profile/apply-exams/tgattpat/update-sites" element={<TGATTPATUpdateSites />} />
          <Route path="/profile/apply-exams/tgattpat/veto" element={<Exam4YVeto examType="tgattpat" />} />

          {/* TCAS70 ไม่มี CBT (G27-4) */}
          <Route path="/profile/apply-exams/tgattpat/cbt" element={<Navigate to="/profile" replace />} />
          <Route path="/profile/apply-exams/tgattpat/:day" element={<TGATTPATDetail />} />

          <Route path="/profile/apply-exams/alevel" element={<ALevelSummary />} />
          <Route path="/profile/apply-exams/alevel/confirmation" element={<ALevelConfirmation />} />
          <Route path="/profile/apply-exams/alevel/update-sites" element={<ALevelUpdateSites />} />
          {/* SIM ซ่อนจนกว่าจะประกาศวัน TCAS70 (API ปิด window alevel.sim แล้ว) — วันใน exam-days.json SIMINFOS ยังเป็นของ TCAS69 */}
          <Route path="/profile/apply-exams/alevel/sim" element={<Navigate to="/profile" replace />} />
          <Route path="/profile/apply-exams/alevel/:day" element={<ALevelDetail />} />

          <Route path="/profile/offer-accept/:roundType/:offerId" element={<OfferAccept />} />
          <Route path="/profile/offer-reject/:roundType" element={<OfferReject />} />
          <Route path="/profile/offer-cancel/:roundType" element={<OfferCancel />} />

          <Route
            path="/profile/exam-application-info/tgattpat"
            element={<ExamApplicationInfo type="tgattpat" />}
          />
          <Route path="/profile/exam-application-info/alevel" element={<ExamApplicationInfo type="alevel" />} />
          <Route path="/profile/exam-scores/tgattpat" element={<ExamScores type="tgattpat" />} />
          <Route path="/profile/exam-scores/alevel" element={<ExamScores type="alevel" />} />
          <Route path="/profile/exam-recheck/tgattpat" element={<ExamRecheck type="tgattpat" />} />
          <Route path="/profile/exam-recheck/alevel" element={<ExamRecheck type="alevel" />} />
          <Route
            path="/profile/exam-recheck-consent/tgattpat"
            element={<ExamRecheckConsent type="tgattpat" />}
          />
          <Route path="/profile/exam-recheck-consent/alevel" element={<ExamRecheckConsent type="alevel" />} />
          <Route path="/profile/exam-answers/tgattpat" element={<ExamAnswers type="tgattpat" />} />
          <Route path="/profile/exam-answers/alevel" element={<ExamAnswers type="alevel" />} />
          <Route path="/profile/exam-answers/:appId" element={<ExamAnswerDetail />} />

          <Route path="/profile/exam-dispute/alevel" element={<ExamDispute type='alevel' />} />
          <Route path="/profile/exam-dispute-consent/alevel" element={<ExamDisputeConsent type='alevel' />} />

          {/* <Route path="/profile/round3" element={<AdmissionPrepare />} /> */}
          <Route path="/profile/redeem-round3" element={<RedeemRound3 />} />
          <Route path="/profile/favorite-round3" element={<AdmissionPrepare />} />
          <Route path="/profile/apply-round3" element={<ApplyRound3 />} />
          <Route path="/profile/set-zero" element={<SetZero />} />
          <Route path="/profile/reprocess-round3/:reprocessKey" element={<ReprocessRound3 />} />

          <Route path="/profile/folios/create" element={<FolioBuilder />} />
          <Route path="/profile/folios/:folioId" element={<FolioBuilder />} />
          <Route path="/profile/rank-check-round3" element={<RankCheckRound3 />} />
        </Route>
        <Route path="/er/:hash" element={<ExamResult type="tgattpat" />} />
        <Route path="/er/:hash/tgattpat" element={<ExamResult type="tgattpat" />} />
        <Route path="/er/:hash/alevel" element={<ExamResult type="alevel" />} />
        <Route path="/r3/:hash" element={<R3RedemptionReceipt />} />
        <Route path="/tgat-redeemed/:hash" element={<TGATRedemptionReceipt />} />

        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
      </Suspense>
    </Router>
  );
}
